import { useRef } from 'react'

import {
  useCreateCollection,
  useUpdateCollection,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

import { Form } from '../Form/index.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

export const CollectionFormModal = ({
  id,
  name,
  description,
  open,
  onClose,
}: {
  id?: string
  name?: string
  description?: string
  open: boolean
  onClose: () => void
}) => {
  const { $t } = useI18n()
  const inputRef = useRef<HTMLInputElement>()
  const { mutateAsync: createCollection, isLoading: isCreating } =
    useCreateCollection({
      fields: 'basic',
    })
  const { mutateAsync: updateCollection, isLoading: isUpdating } =
    useUpdateCollection()
  const isNew = !id

  return (
    <Modal open={open} onClose={onClose} initialFocus={inputRef}>
      <Modal.Header
        title={
          isNew ? (
            <T
              id="Collection.Create"
              defaultMessage="Create a new {COLLECTION}"
            />
          ) : (
            <T id="Collection.Edit" defaultMessage="Edit {COLLECTION}" />
          )
        }
      />
      <Modal.Content>
        <Form
          defaultValues={{ name, description }}
          onSubmit={async data => {
            const { name, description } = data
            const input = {
              name,
              description,
            }

            if (id) {
              return updateCollection({
                id,
                input,
              }).then(() => {
                onClose()
              })
            }

            return createCollection({
              input,
            }).then(() => {
              onClose()
            })
          }}
        >
          <div className="flex flex-col space-y-5">
            <Form.Input
              ref={inputRef}
              label={<T defaultMessage="Name" id="Generics.Name.Noun" />}
              name="name"
              placeholder={$t({
                defaultMessage: 'Choose a helpful name (e.g. Resources)',
                id: 'CollectionForm.Input.Name.Placeholder',
              })}
              validation={{
                required: $t({
                  defaultMessage: 'Please enter a name for the {COLLECTION}',
                  id: 'CollectionForm.Input.Name.Validation.Required',
                }),
              }}
            />
            <Form.Textarea
              label={
                <T defaultMessage="Description" id="Generics.Description" />
              }
              name="description"
              placeholder={$t({
                defaultMessage:
                  'What is this {COLLECTION} about? (e.g. All the resources you need to get started)',
                id: 'CollectionForm.Input.Description.Placeholder',
              })}
            />
            <Form.Actions>
              <Button
                variant="primary"
                type="submit"
                loading={isCreating || isUpdating}
              >
                {isNew ? (
                  <T defaultMessage="Create" id="Generics.Create" />
                ) : (
                  <T defaultMessage="Update" id="Generics.Update" />
                )}
              </Button>
              <Button variant="secondaryNeutral" onClick={onClose}>
                <T defaultMessage="Cancel" id="Generics.Cancel" />
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  )
}
